import type { ChangeEvent } from 'react'

import type { UseFormPayload } from '@domain-group/react-hooks'

import { getFullAddress } from '@functions/address'

import { useInvoiceForm } from '@hooks/useInvoice'
import useCurrentUser from '@hooks/useCurrentUser'
import useOrders from '@hooks/useOrders'
import useSelector from '@hooks/useSelector'

import type {
  AgencyModel, CampaignModel, InvoiceModel, OrderModel, ProviderModel,
} from '@models/types'

type SetEntityState = UseFormPayload['setEntityState']

const getEntityString = (string: string) => {
  if (string === 'Agency') return 'agencies'
  if (string === 'Brand') return 'brands'
  if (string === 'Provider') return 'providers'
  return ''
}

type HandleOrderChangeParams = {
    campaigns: Record<string, CampaignModel>,
    e: ChangeEvent<HTMLSelectElement>,
    filteredOrders: OrderModel[],
    setEntityState: SetEntityState,
}

const handleOrderChange = (params: HandleOrderChangeParams) => {
  const {
    campaigns, e, filteredOrders, setEntityState,
  } = params

  const selectedOrderId = e.target.value

  // If no order is selected, clear order_title and reference
  if (!selectedOrderId){
    setEntityState({
      order_id: '',
      order_title: '',
      reference: '',
    })
    return
  }

  // Otherwise, find the selected order and update the fields
  const selectedOrder = filteredOrders.find(
    order => Number(order.id) === Number(selectedOrderId),
  )

  if (selectedOrder){
    setEntityState({
      order_id: selectedOrder.id,
      order_title: selectedOrder.title,
      reference: campaigns[selectedOrder.campaign_id]?.address ?? '',
    })
  }
}

type Entity = Partial<ProviderModel | AgencyModel>;

const buildAddress = (entity: Entity | null) => {
  if (!entity) return ''

  const address = getFullAddress(
    {
      address_line_1: entity.address_line_1,
      address_line_2: entity.address_line_2,
      address_postcode: entity.address_postcode,
      address_region: entity.address_region,
      address_state: entity.address_state,
    },
    { multiLine: true },
  )

  const formattedAddress = `${entity.title}\n${address}`

  return formattedAddress
}

type HandleEntityStateChangeParams = {
    e: ChangeEvent<HTMLSelectElement> | Entity,
    provider?: Partial<ProviderModel>,
    setEntityState: SetEntityState,
}

const handleEntityStateChange = (params: HandleEntityStateChangeParams) => {
  const { e, provider, setEntityState } = params

  // @ts-expect-error TS2339
  const eventValue = e.target.value
  const isTypeProvider = !!provider?.id && eventValue === 'Provider'

  setEntityState({
    address: isTypeProvider ? buildAddress(provider) : '',
    debtor_id: isTypeProvider ? provider?.id : '',
    debtor_name: isTypeProvider ? provider?.title : '',
    debtor_type: eventValue,
    order_id: '',
    order_title: '',
    purchase_order: '',
    reference: '',
  })
}

type HandleStateParams = {
    editing: boolean | undefined,
    entity: Entity | null,
    setEntityState: SetEntityState,
  }

const handleEntitySelectorChange = (params: HandleStateParams) => {
  const { editing = false, entity, setEntityState } = params

  const updatedState = {
    address: buildAddress(entity),
    debtor_id: entity?.id || '',
    debtor_name: entity?.title || '',
    debtor_type: entity?.type || '',
  }

  if (editing){
    setEntityState((prevState: Record<string, string | number>) => ({ ...prevState, ...updatedState }))
  }

  setEntityState({
    ...updatedState,
    order_id: '',
    order_title: '',
    purchase_order: '',
    reference: '',
  })
}

const customRequiredFields = [
  { key: 'debtor_id' },
  { key: 'order_title' },
  { key: 'reference' },
]

type ManageInvoiceOptions = {
  invoice: InvoiceModel,
}

function useManageInvoice(options: ManageInvoiceOptions) {
  const { invoice } = options || {}

  const currentUserPayload = useCurrentUser({})
  const { isAdmin, isProviderDirector, provider } = currentUserPayload

  const invoiceFormPayload = useInvoiceForm(invoice, {
    customRequiredFields,
    validateOn: 'change',
  })

  const {
    entityState: { debtor_id, debtor_type },
    setEntityState,
  } = invoiceFormPayload
  const campaigns = useSelector(reduxState => reduxState.entities.campaigns)

  const ordersPayload = useOrders({
    filters: {
      agency_id: debtor_type === 'Agency' ? debtor_id : null,
    },
    requestOptions: {
      include_order_campaign: true,
    },
    performHttpRequests: !!debtor_id && debtor_type === 'Agency',
  })

  const { filteredOrders } = ordersPayload

  return {
    callbacks: {
      getEntityString: (string: string) => getEntityString(string),
      handleEntitySelectorChange: ({ entity, editing } :
        { entity: Entity | null, editing?: boolean}) => handleEntitySelectorChange({
        editing,
        entity,
        setEntityState,
      }),
      handleEntityStateChange: (e: ChangeEvent<HTMLSelectElement>) => handleEntityStateChange({
        e, setEntityState, provider,
      }),
      handleOrderChange: (e: ChangeEvent<HTMLSelectElement>) => handleOrderChange({
        e, filteredOrders, setEntityState, campaigns,
      }),
    },
    filteredOrders,
    invoiceFormPayload,
    isAdmin,
    isProviderDirector,
    provider,
  }
}

export default useManageInvoice
